/* eslint-disable */
// src/utils/visitMappers.js

/* 
 * toFormMachine: API -> shape de UI para edición (nombres consistentes).
 * toPayloadFromForm: UI -> payload para create/update (API-friendly).
 * calculateTotals: suma totales agrupando por cliente/dueño, etc.
 * Punto único de transformación de datos entre UI y backend.
 */

import { hasRent, getRent } from "utils/rent";
import { formatFormulaLabel } from "utils/formula";
import {
  isInsertedInCurrentVisitNoticeRow,
  isPersistedCollectedMachine,
} from "utils/visitCollectionState";

const COLLECTION_RESULT_FIELDS = [
  "CreditsIn",
  "CreditsOut",
  "NetCreditsIn",
  "NetCreditsOut",
  "CreditsCollected",
  "MoneyIn",
  "MoneyOut",
  "MoneyCollected",
  "ClientAmount",
  "OwnerAmount",
  "NetMoneyIn",
  "NetMoneyOut",
];

const toNum = (v) => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Convierte la máquina que viene del backend al shape que usa tu UI/form */
export function toFormMachine(m) {
  const machineTypeName = m?.MachineType?.Type || m?.MachineType || null;
  const isCash = machineTypeName === "Rockola";
  const hasRentFlag = hasRent(m);

  // Si no es Rockola ni alquilada, exponemos Formula en el shape de UI
  const shouldExposeFormula = !isCash && !hasRentFlag;

  const fType = String(m.FormulaType ?? m.Formula?.Type ?? "").trim();
  const hasOperator = ["+", "-", "*", "/"].includes(fType);

  const Formula =
    shouldExposeFormula && (m.FormulaId != null || hasOperator)
      ? {
          Id: toNum(m.FormulaId),
          Type: m.FormulaType ?? (m.Formula?.Type ?? null),
          Value: toNum(m.FormulaValue ?? m.Formula?.Value),
        }
      : null;

  return {
    Id: m.Id,
    LastVisitMachineId: m.Id,
    LocalMachineId: m.LocalMachineId,
    LocalDisplayNumber: toNum(m.LocalDisplayNumber),

    Machine: {
      Name: m.MachineName,
      SerialNumber: m.SerialNumber,
      LocalDisplayNumber: toNum(m.LocalDisplayNumber),
    },
    MachineType: { Type: m.MachineType, Id: m.MachineTypeId },

    Formula,
    FormulaLabel: shouldExposeFormula ? formatFormulaLabel(m, { fallback: null }) : null,

    Rent: hasRentFlag ? getRent(m) : null,

    // Campos que tu UI espera:
    actualCreditsIn: toNum(m.CreditsIn),
    actualCreditsOut: toNum(m.CreditsOut),
    netCreditsIn: toNum(m.NetCreditsIn),
    netCreditsOut: toNum(m.NetCreditsOut),

    moneyIn: toNum(m.MoneyIn),
    moneyOut: toNum(m.MoneyOut),
    netMoneyIn: toNum(m.NetMoneyIn),
    netMoneyOut: toNum(m.NetMoneyOut),
    moneyCollected: toNum(m.MoneyCollected),
    clientAmount: toNum(m.ClientAmount),
    ownerAmount: toNum(m.OwnerAmount),

    // flag real traído del backend
    WasCollected: isPersistedCollectedMachine(m),
    IsAccumulative: Boolean(m.IsAccumulative),
    HasReset: Boolean(m.HasReset),
    PostResetStartIn: toNum(m.PostResetStartIn),
    PostResetStartOut: toNum(m.PostResetStartOut),
    PrevVisitId: toNum(m.PrevVisitId),
    PrevVisitDate: m.PrevVisitDate ?? null,

    note: m.Note || "",
    LocalMachineProfitPercentageId: m.LocalMachineProfitPercentageId,
    HistoricalProfitPercentage:
      m.HistoricalProfitPercentage ??
      (m.LocalMachineProfitPercentageId != null &&
      (m.ClientPercentage != null || m.OwnerPercentage != null)
        ? {
            Id: m.LocalMachineProfitPercentageId,
            ClientPercentage: toNum(m.ClientPercentage),
            OwnerPercentage: toNum(m.OwnerPercentage),
          }
        : null),
    CounterVerification: m.CounterVerification ?? null,
  };
}


/** Convierte del shape de UI al payload de guardado/edición */
export function toPayloadFromForm(m, visitWithCollectionId) {
  if (isInsertedInCurrentVisitNoticeRow(m)) return null;

  const isCash = m?.MachineType?.Type === "Rockola";
  const hasRent = Boolean(m?.Rent?.Id ?? m?.RentId);
  const wasCollected = isPersistedCollectedMachine(m);

  const payload = {
    LocalMachineId:      Number(m.LocalMachineId),
    Note:                m.note || null,
    WasCollected:        wasCollected,
    CreditsIn:           m.actualCreditsIn  ?? null,
    CreditsOut:          m.actualCreditsOut ?? null,
    NetCreditsIn:        m.netCreditsIn      ?? null,
    NetCreditsOut:       m.netCreditsOut     ?? null,
    CreditsCollected:    m.creditsCollected  ?? null,
    MoneyIn:             m.moneyIn           ?? null,
    MoneyOut:            m.moneyOut          ?? null,
    MoneyCollected:      m.moneyCollected    ?? null,
    ClientAmount:        m.clientAmount      ?? null,
    OwnerAmount:         m.ownerAmount       ?? null,
    VisitWithCollectionId: Number(visitWithCollectionId),
    LocalMachineProfitPercentageId: m.LocalMachineProfitPercentageId,
    MachineTypeId: Number(m.MachineType?.Id),
    FormulaId: (hasRent || isCash) ? null : (m?.Formula?.Id ?? m?.FormulaId ?? null),
    RentId: (m.Rent?.Id ?? m.RentId) ? Number(m.Rent?.Id ?? m.RentId) : null,
    NetMoneyIn:   m.netMoneyIn   ?? null,
    NetMoneyOut:  m.netMoneyOut  ?? null,
    HasReset: wasCollected ? Boolean(m.HasReset) : false,
    PostResetStartIn: wasCollected && Boolean(m.HasReset) ? (m.PostResetStartIn ?? null) : null,
    PostResetStartOut: wasCollected && Boolean(m.HasReset) ? (m.PostResetStartOut ?? null) : null,
  };

  if (!wasCollected) {
    COLLECTION_RESULT_FIELDS.forEach((field) => {
      payload[field] = null;
    });
    payload.HasReset = false;
    payload.PostResetStartIn = null;
    payload.PostResetStartOut = null;
  }

  return payload;
}

export function calculateTotals(arr) {
  return arr.reduce((acc,m) => {
    if (!isPersistedCollectedMachine(m)) return acc;
    return {
      TotalAmount:       acc.TotalAmount       + (m.moneyCollected || 0),
      TotalClientAmount: acc.TotalClientAmount + (m.clientAmount   || 0),
      TotalOwnerAmount:  acc.TotalOwnerAmount  + (m.ownerAmount    || 0),
    };
  }, { TotalAmount:0, TotalClientAmount:0, TotalOwnerAmount:0 });
}
